import { useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import { useFetchRawPermissionsQuery } from '@/query/role.query';

import type { UpdateRoleFormValues } from '@/schemas/role.schema';

import { useFormikError } from '@/lib/error';

import type { Permission } from '@/types/role.type';

type Props = {
  selectedPermissions: Permission[];
  roleId: number;
};

const formatGroupName = (prefix: string) =>
  prefix.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const groupPermissions = (permissions: Permission[]) => {
  const groups = new Map<string, Permission[]>();
  for (const p of permissions) {
    const prefix = p.name.split(':')[0];
    if (!groups.has(prefix)) groups.set(prefix, []);
    groups.get(prefix)!.push(p);
  }
  return groups;
};

export const RoleUpdateForm = ({ selectedPermissions, roleId }: Props) => {
  const { getFieldProps, errors, setFieldValue } = useFormikContext<UpdateRoleFormValues>();
  const hasError = useFormikError();

  const { data } = useFetchRawPermissionsQuery();
  const grouped = useMemo(() => groupPermissions(data || []), [data]);

  const initialPermissionIds = useMemo(
    () => new Set(selectedPermissions.map(p => p.id.toString())),
    [selectedPermissions]
  );

  const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(
    () => new Set(selectedPermissions?.map(p => p.id.toString()))
  );

  const { permissionsToAdd, permissionsToRemove } = useMemo(() => {
    const toAdd = Array.from(selectedPermissionIds)
      .filter(id => !initialPermissionIds.has(id))
      .map(Number);

    const toRemove = Array.from(initialPermissionIds)
      .filter(id => !selectedPermissionIds.has(id))
      .map(Number);

    return { permissionsToAdd: toAdd, permissionsToRemove: toRemove };
  }, [selectedPermissionIds, initialPermissionIds]);

  useEffect(() => {
    setFieldValue('add_permission_ids', permissionsToAdd);
    setFieldValue('remove_permission_ids', permissionsToRemove);
    setFieldValue(
      'hasPermissionsSelected',
      selectedPermissionIds.size > 0 || permissionsToAdd.length > 0
    );
  }, [permissionsToAdd, permissionsToRemove, selectedPermissionIds]);

  const handlePermissionChange = (isSelected: boolean, permissionId: string) => {
    setSelectedPermissionIds(prev => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(permissionId);
      } else {
        next.delete(permissionId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <Field aria-invalid={hasError('name')}>
          <RequiredLabel htmlFor="name" className="mb-1">
            Role name
          </RequiredLabel>
          <Input
            id="name"
            {...getFieldProps('name')}
            placeholder="Enter role name ..."
            aria-invalid={hasError('name')}
            maxLength={20}
          />
          <ErrorField name="name" />
        </Field>
      </div>

      <div className="flex flex-col gap-2">
        <Field aria-invalid={hasError('description')}>
          <RequiredLabel htmlFor="description" className="mb-1">
            Role description
          </RequiredLabel>
          <Input
            id="description"
            {...getFieldProps('description')}
            placeholder="Add description"
            aria-invalid={hasError('description')}
            maxLength={150}
          />
          <ErrorField name="description" />
        </Field>
      </div>

      <div className="flex flex-col gap-2">
        <RequiredLabel className="mb-1">Permissions</RequiredLabel>
        <div className="space-y-4">
          {Array.from(grouped.entries()).map(([group, perms]) => (
            <div key={group} className="px-3 py-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                {formatGroupName(group)}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {perms.map(p => (
                  <label
                    key={p.id}
                    htmlFor={`role-${roleId}-perm-${p.id}`}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Checkbox
                      id={`role-${roleId}-perm-${p.id}`}
                      checked={selectedPermissionIds.has(p.id.toString())}
                      onCheckedChange={isSelected =>
                        handlePermissionChange(!!isSelected, p.id.toString())
                      }
                    />
                    <span className="text-sm">{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        {errors.hasPermissionsSelected && (
          <p className="text-sm text-destructive"> {errors.hasPermissionsSelected} </p>
        )}
      </div>
    </div>
  );
};
